/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  CustomColumnHeader,
  CustomColumnItem,
  EventRequest,
  EventState,
  getAdderColumnType,
  ID,
  LineItemChanges,
  LotChanges,
} from 'types';
import {
  BASELINE_PRICE_SOURCE_COLUMN_KEY,
  EXTENDED_QUANTITY_COLUMN_KEY,
  ID_COLUMN_KEY,
  IS_REQUIRED_COLUMN_KEY,
  NAME_COLUMN_KEY,
  NEGOTIATION_PRICE_SOURCE_COLUMN_KEY,
  NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
  PRICE_COLUMN_KEY,
  QUANTITY_COLUMN_KEY,
  RESOLVED_LINE_ITEM_NAME_COLUMN_KEY,
  SNO_COLUMN_KEY,
  SUGGESTED_PRICE_COLUMN_KEY,
  SYSTEM_MODIFIED_COLUMN_KEY,
  UNITS_COLUMN_KEY,
  IS_BUYER_PROVIDED_BASELINE_PRICE_COLUMN_KEY,
} from '@components/bid_request/details/constants';
import { hasEventChanges, matchesNew } from '@components/bid_request/details/utils';
import {
  CustomColumnItemField,
  HeaderField,
  LineItemField,
  LotField,
  PayloadChanges,
  PayloadCustomColumnItem,
  PayloadHeader,
  PayloadLineItem,
  PayloadLot,
} from './types';

export const parseCustomColumnItemModel = ({
  customColumnItem,
  lineItemId,
}: {
  customColumnItem: CustomColumnItem,
  lineItemId: ID,
}): PayloadCustomColumnItem => {
  if (!customColumnItem || !Object.keys(customColumnItem)?.length) return {};
  const customColumnItemResult: PayloadCustomColumnItem = {};
  const fields: CustomColumnItemField[] = [
    'column_type',
    'custom_column_header_id',
    'display_text',
    'id',
    'is_supplier_visible',
    'line_item_id',
    'qualifying_equality',
  ];

  fields.forEach((field: CustomColumnItemField): void => {
    if (field in customColumnItem && customColumnItem[field] !== undefined) {
      customColumnItemResult[field] = customColumnItem[field] as any;
    }
  });

  customColumnItemResult.line_item_id = lineItemId;

  if (customColumnItem.answer_option !== undefined) {
    customColumnItemResult.answer_option_id = customColumnItem.answer_option;
  }

  return customColumnItemResult;
};

const getCustomColumnItems = ({
  lineItemId,
  customColumnItems,
  customColumnHeaders,
}: {
  customColumnHeaders: Record<ID, CustomColumnHeader>,
  customColumnItems: Record<ID, CustomColumnItem>,
  lineItemId: ID,
}): Record<ID, PayloadCustomColumnItem> => {
  if (!customColumnItems || !Object.keys(customColumnItems)?.length) return {};
  const customColumnItemsResult: Record<ID, PayloadCustomColumnItem> = {};

  Object.entries(customColumnItems).forEach(([id, customColumnItem]) => {
    const header = customColumnHeaders[customColumnItem.custom_column_header_id];

    // if we don't have a changed header, that means it's not marked for destruction
    // Skip the custom column item if the header is marked for destruction
    if (header?._destroy) return;
    const parsedItem = parseCustomColumnItemModel({
      customColumnItem,
      lineItemId,
    });

    // The header is authoritative for a custom cell's type. Spreadsheet imports
    // historically initialized every cell as descriptive, including cells that
    // belong to adder headers.
    if (header?.column_type !== undefined) parsedItem.column_type = header.column_type;

    customColumnItemsResult[id] = parsedItem;
  });

  return customColumnItemsResult;
};

const CUSTOM_COLUMN_HEADER_FIELDS = [
  'column_order_index',
  'column_type',
  'data_type',
  'display_text',
  'id',
  'is_supplier_visible',
  'lot_id',
] as const;

const parseHeaderModel = ({
  lotId,
  header,
}: {
  header: CustomColumnHeader;
  lotId: ID;
}): PayloadHeader => {
  if (!header || !Object.keys(header)?.length) return {};
  if (header._destroy) return { _destroy: true };
  const headerResult: PayloadHeader = {};

  CUSTOM_COLUMN_HEADER_FIELDS.forEach((field: HeaderField): void => {
    if (field in header && header[field] !== undefined) {
      headerResult[field] = header[field] as any;
    }
  });

  headerResult.lot_id = lotId;

  if (header.answer_option !== undefined) {
    headerResult.answer_option_id = header.answer_option;
  }
  if (header.qual_question?.id !== undefined) {
    headerResult.qual_question_id = header.qual_question.id;
  }

  if (header.column_type === getAdderColumnType() && header.adder_column_config) {
    headerResult.adder_column_config = {
      multiplier_operand_source: header.adder_column_config.multiplier_operand_source ?? null,
      rate_type: header.adder_column_config.rate_type ?? null,
      scope: header.adder_column_config.scope,
      taxonomy: header.adder_column_config.taxonomy ?? null,
    };
  }

  return headerResult;
};

const LOT_FIELDS = [
  '_destroy',
  'deleted_qualifying_custom_column_headers',
  'custom_column_headers',
  'description',
  'id',
  'incumbent_id',
  'line_items',
  'name',
  'sno'
] as const;

const parseLineItemModel = ({
  lotId,
  lineItemId,
  lineItem,
  customColumnHeaders,
}: {
  customColumnHeaders: Record<ID, CustomColumnHeader>,
  lineItem: LineItemChanges,
  lineItemId: ID,
  lotId: ID,
}): PayloadLot => {
  if (!lineItem || !Object.keys(lineItem)?.length) return {};
  const lineItemResult: PayloadLineItem = {};
  const fields: LineItemField[] = [
    '_destroy',
    BASELINE_PRICE_SOURCE_COLUMN_KEY,
    EXTENDED_QUANTITY_COLUMN_KEY,
    ID_COLUMN_KEY,
    IS_REQUIRED_COLUMN_KEY,
    NAME_COLUMN_KEY,
    NEGOTIATION_PRICE_SOURCE_COLUMN_KEY,
    NEGOTIATION_STARTING_PRICE_COLUMN_KEY,
    PRICE_COLUMN_KEY,
    QUANTITY_COLUMN_KEY,
    RESOLVED_LINE_ITEM_NAME_COLUMN_KEY,
    SNO_COLUMN_KEY,
    SUGGESTED_PRICE_COLUMN_KEY,
    SYSTEM_MODIFIED_COLUMN_KEY,
    UNITS_COLUMN_KEY,
    IS_BUYER_PROVIDED_BASELINE_PRICE_COLUMN_KEY,
  ];

  fields.forEach((field: LineItemField): void => {
    if (field in lineItem && lineItem[field] !== undefined) {
      lineItemResult[field] = lineItem[field] as any;
    }
  });

  lineItemResult.lot_id = lotId;
  lineItemResult.id = lineItemId;

  if (lineItem.custom_column_items && Object.keys(lineItem.custom_column_items).length > 0) {
    const customColumnItems = lineItem.custom_column_items;

    lineItemResult.custom_column_items = getCustomColumnItems({
      customColumnHeaders,
      customColumnItems,
      lineItemId,
    });
  }

  return lineItemResult;
};

const getHeaders = ({
  lotId,
  headers,
}: {
  headers: Record<ID, CustomColumnHeader>,
  lotId: ID,
}): Record<ID, PayloadHeader> => {
  if (!headers || !Object.keys(headers)?.length) return {};
  const headersResult: Record<ID, PayloadHeader> = {};

  Object.entries(headers).forEach(([headerId, header]) => {
    if (matchesNew(headerId) && header._destroy) return;

    headersResult[headerId] = parseHeaderModel({ header, lotId });
  });

  return headersResult;
};

const getLineItems = ({
  lotId,
  customColumnHeaders,
  lineItems,
}: {
  customColumnHeaders: Record<ID, CustomColumnHeader>,
  lineItems: Record<ID, LineItemChanges>,
  lotId: ID,
}): Record<ID, PayloadLot> => {
  if (!lineItems || !Object.keys(lineItems)?.length) return {};
  const lineItemsResult: Record<ID, PayloadLot> = {};

  Object.entries(lineItems).forEach(([lineItemId, lineItem]) => {
    if (matchesNew(lineItemId) && lineItem._destroy) return;

    lineItemsResult[lineItemId] = parseLineItemModel({
      customColumnHeaders,
      lineItem,
      lineItemId,
      lotId,
    });
  });

  return lineItemsResult;
};

const parseLotModel = ({
  lotChange,
  eventIncumbentId,
}: {
  eventIncumbentId?: ID | null,
  lotChange: LotChanges,
}): Partial<PayloadLot> => {
  if (!lotChange || !Object.keys(lotChange)?.length) return {};

  const lotChangeResult: Partial<PayloadLot> = {};

  LOT_FIELDS.forEach((field: LotField): void => {
    if (field in lotChange && lotChange[field] !== undefined) {
      lotChangeResult[field] = lotChange[field] as any;
    }
  });

  if (lotChange.custom_column_headers) {
    lotChangeResult.custom_column_headers = getHeaders({
      headers: lotChange.custom_column_headers,
      lotId: lotChange.id,
    });
  }
  if (lotChange.line_items) {
    lotChangeResult.line_items = getLineItems({
      customColumnHeaders: lotChange.custom_column_headers || {},
      lineItems: lotChange.line_items,
      lotId: lotChange.id,
    });
  }

  if (eventIncumbentId && (matchesNew(eventIncumbentId) || Number(eventIncumbentId) > 0)) {
    lotChangeResult.incumbent_id = eventIncumbentId;
  }

  return lotChangeResult;
};

const parseEventModel = (event?: EventState): Partial<EventRequest> => {
  if (!event) return {};
  const { currency, eventIncumbentId } = event;
  const request: Partial<EventRequest> = {};

  if (eventIncumbentId && (matchesNew(eventIncumbentId) || Number(eventIncumbentId) > 0)) {
    request.overall_incumbent_id = eventIncumbentId;
  } else {
    request.overall_incumbent_id = null;
  }

  if (currency) {
    request.currency = currency;
  }

  return request;
};

export const prepareChanges = (
  lotChanges?: Record<ID, LotChanges>,
  event?: EventState,
): PayloadChanges => {
  if (!event && (!lotChanges || !Object.keys(lotChanges)?.length)) return {};
  const isEventChanged = hasEventChanges({ event });
  let changes: PayloadChanges = {};

  // If we have lot changes, add them to changes object
  if (lotChanges) {
    const lots: Record<ID, PayloadLot> = {};

    Object.entries(lotChanges).forEach(([lotId, lotChange]) => {
      if (matchesNew(lotId) && lotChange._destroy) return;
      const eventIncumbentId = isEventChanged ? event?.eventIncumbentId : null;

      lots[lotId] = parseLotModel({ eventIncumbentId, lotChange });
    });

    changes = { lots };
  }

  if (isEventChanged) {
    changes = { ...changes, request: parseEventModel(event) };
  }

  return changes;
};
